import {
  $isBeautifulMentionNode,
  type BeautifulMentionComponentProps,
} from 'lexical-beautiful-mentions';
import { FireIcon } from '@terra/ui-icons';
import {
  forwardRef,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent,
} from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getNearestNodeFromDOMNode } from 'lexical';
import { useTranslations } from 'next-intl';
import { Text } from '../../../../../components/styled/text';
import type { MentionOption } from '../types';
import { useMentions } from '../../../mentions/ChatMentionsProvider';
import { resolveMentionEntityIcon } from '../../../mentions/mentionEntityIcons';
import { MentionOverflowBadge } from './MentionOverflowBadge';
import { useMentionOverflowElements } from './MentionOverflowContext';
import type { MentionOverflowEntry } from './mentionOverflow';
import { ContextPill } from '../../../context-pill/ContextPill';
import { MentionContextPill } from '../../../context-pill/MentionContextPill';
import type {
  MentionEntity,
  ObjectContextKind,
} from '@terra/shared-dto-chat-types';

function isStringRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readStoredMentionExtraData(
  extraDataJson: string | undefined,
): MentionEntity['extraData'] | undefined {
  if (!extraDataJson) {
    return undefined;
  }

  try {
    const parsed: unknown = JSON.parse(extraDataJson);

    if (isStringRecord(parsed)) {
      return parsed;
    }
  } catch {
    return undefined;
  }

  return undefined;
}

const LEGACY_ICONS = {
  signal: resolveMentionEntityIcon({ entityType: 'Signal' }),
  smoke: FireIcon,
  finding: resolveMentionEntityIcon({ entityType: 'Finding' }),
};

export const MentionComponent = forwardRef<
  HTMLSpanElement,
  BeautifulMentionComponentProps<MentionOption>
>(
  (
    {
      trigger: _trigger,
      value,
      children: _children,
      className: _className,
      data,
      ...rest
    },
    ref,
  ) => {
    const tCommon = useTranslations('designSystem.common');
    const label = data?.displayValue || data?.label || value;
    const entityType = data?.entityType as
      | MentionEntity['entityType']
      | undefined;
    const objectKind = data?.objectKind as ObjectContextKind | undefined;
    const legacyType = data?.type as keyof typeof LEGACY_ICONS | undefined;
    const legacyIcon =
      !entityType && legacyType ? LEGACY_ICONS[legacyType] : undefined;
    const [editor] = useLexicalComposerContext();
    const { onMentionClick, removeMention, removeMentionByEntityId } =
      useMentions();
    const overflowByElement = useMentionOverflowElements();
    const [overflowEntry, setOverflowEntry] = useState<
      MentionOverflowEntry | undefined
    >(undefined);
    const mentionElementRef = useRef<HTMLSpanElement | null>(null);
    const setMentionElementRef = useCallback(
      (element: HTMLSpanElement | null) => {
        mentionElementRef.current = element;
        if (typeof ref === 'function') {
          ref(element);
        } else if (ref) {
          ref.current = element;
        }
      },
      [ref],
    );

    useLayoutEffect(() => {
      let element: HTMLElement | null = mentionElementRef.current;
      let entry: MentionOverflowEntry | undefined;

      while (element && !entry) {
        entry = overflowByElement.get(element);
        element = element.parentElement;
      }

      setOverflowEntry(entry);
    }, [overflowByElement]);
    const entityId =
      typeof data?.entityId === 'string' ? data.entityId : undefined;
    const mentionMenuItemId =
      typeof data?.mentionMenuItemId === 'string'
        ? data.mentionMenuItemId
        : undefined;
    const canRemove = Boolean(mentionMenuItemId || entityId);

    if (overflowEntry?.kind === 'hidden') {
      return (
        <Text as="span" display="none" ref={setMentionElementRef} {...rest} />
      );
    }

    const handleRemove = (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      const mentionElement = mentionElementRef.current;

      if (mentionMenuItemId) {
        removeMention(mentionMenuItemId);
      } else if (entityId) {
        removeMentionByEntityId(entityId);
      }

      if (mentionElement) {
        editor.update(() => {
          const node = $getNearestNodeFromDOMNode(mentionElement);

          if ($isBeautifulMentionNode(node)) {
            node.remove();
          }
        });
      }
    };

    const removeAriaLabel = tCommon('delete');
    const storedExtraData = readStoredMentionExtraData(data?.extraDataJson);
    const extraData =
      storedExtraData ??
      (entityType === 'ObjectContext' && objectKind && entityId
        ? {
            objectContext: {
              objectKind,
              objectId: entityId,
              objectLabel: label,
              rawText: label,
            },
          }
        : undefined);
    const pill =
      entityType && entityId ? (
        <MentionContextPill
          ref={setMentionElementRef}
          {...rest}
          entityType={entityType}
          entityId={entityId}
          label={label}
          extraData={extraData}
          removeAriaLabel={removeAriaLabel}
          onRemove={canRemove ? handleRemove : undefined}
          onClick={() => {
            onMentionClick?.({
              entityType,
              entityId,
              label,
            });
          }}
        />
      ) : (
        <ContextPill
          ref={setMentionElementRef}
          {...rest}
          label={label}
          icon={legacyIcon}
          removeAriaLabel={removeAriaLabel}
          onRemove={canRemove ? handleRemove : undefined}
          tone="mention"
        />
      );

    return (
      <>
        {pill}
        {overflowEntry?.kind === 'overflowAnchor' ? (
          <MentionOverflowBadge
            overflowMentions={overflowEntry.overflowMentions}
          />
        ) : null}
      </>
    );
  },
);
MentionComponent.displayName = 'MentionComponent';
